/**
 * Misc demo handlers covering: risk policies, preferences, users (admin), audit, signals.
 */
import { http, HttpResponse } from 'msw';
import { DEMO_RISK_POLICIES, DEMO_USER } from '../demo-fixtures';

const BASE = import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000';

export const demoMiscHandlers = [
  // ── Risk policies ─────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/risk-policy/bots/:id`, ({ params }) => {
    const id = Number(params['id']);
    return HttpResponse.json(DEMO_RISK_POLICIES[id] ?? DEMO_RISK_POLICIES[1]);
  }),

  http.put(`${BASE}/api/v1/risk-policy/bots/:id`, async ({ request, params }) => {
    const id = Number(params['id']);
    const body = (await request.json()) as object;
    return HttpResponse.json({
      ...(DEMO_RISK_POLICIES[id] ?? DEMO_RISK_POLICIES[1]),
      ...(body as object),
      updated_at: new Date().toISOString(),
    });
  }),

  // ── User preferences ─────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/preferences`, () => {
    return HttpResponse.json({
      user_id: DEMO_USER.id,
      theme: 'dark',
      language: 'en',
      notifications_enabled: true,
      email_alerts: false,
      telegram_enabled: false,
      webhook_alert_enabled: false,
      webhook_url: null,
    });
  }),

  http.put(`${BASE}/api/v1/preferences`, async ({ request }) => {
    const body = (await request.json()) as object;
    return HttpResponse.json({
      user_id: DEMO_USER.id,
      ...body,
    });
  }),

  // ── Users (admin) — demo OPERATOR role can view but not create/delete ─────
  http.get(`${BASE}/api/v1/users`, () => {
    return HttpResponse.json({
      items: [DEMO_USER],
      total: 1,
      limit: 50,
      offset: 0,
    });
  }),

  http.get(`${BASE}/api/v1/users/:id`, () => {
    return HttpResponse.json(DEMO_USER);
  }),

  // Creating users requires ADMIN — return 403 in demo
  http.post(`${BASE}/api/v1/users`, () => {
    return HttpResponse.json(
      { detail: 'Demo mode: user management requires ADMIN role' },
      { status: 403 },
    );
  }),

  // ── Audit trail ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/audit`, () => {
    const events = [
      {
        id: 1,
        event_type: 'BOT_STARTED',
        actor: 'user:42',
        resource: 'bot:1',
        payload: { bot_id: 1, bot_name: '[DEMO] Momentum RSI — BTC/USDT' },
        created_at: new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString(),
      },
      {
        id: 2,
        event_type: 'BOT_HALTED',
        actor: 'system:circuit-breaker',
        resource: 'bot:3',
        payload: { bot_id: 3, reason: 'Daily loss limit exceeded: -2.3%' },
        created_at: new Date(Date.now() - 50 * 3600 * 1000).toISOString(),
      },
      {
        id: 3,
        event_type: 'BOT_CREATED',
        actor: 'user:42',
        resource: 'bot:3',
        payload: { bot_id: 3, bot_name: '[DEMO] Breakout Grid — SOL/USDT' },
        created_at: '2026-01-10T08:00:00Z',
      },
    ];
    return HttpResponse.json({ items: events, total: events.length, limit: 50, offset: 0 });
  }),

  // ── Signals ───────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/signals`, () => {
    return HttpResponse.json({ items: [], total: 0, limit: 50, offset: 0 });
  }),

  // ── Strategies catalog ────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/strategies`, () => {
    return HttpResponse.json({
      strategies: [
        {
          name: 'RSI_STRATEGY',
          display_name: 'RSI Strategy',
          description: 'RSI momentum strategy',
        },
        {
          name: 'MACD_STRATEGY',
          display_name: 'MACD Strategy',
          description: 'MACD crossover strategy',
        },
        {
          name: 'GRID_STRATEGY',
          display_name: 'Grid Strategy',
          description: 'Grid trading strategy',
        },
        {
          name: 'BREAKOUT_STRATEGY',
          display_name: 'Breakout Strategy',
          description: 'Price breakout strategy',
        },
      ],
    });
  }),
];
